// Per-user humor/tone matching — pure logic, no Firebase (unit-testable).
// We learn from the user's REACTIONS to the humor itself (not disguised
// questions): a lightweight bandit that nudges a playfulness dial and grows
// confidence with each reaction. Confidence-gated so low evidence stays near the
// safe dry-wit default. See docs/CogniPrint_Quizmaster_Persona_and_Tone_Design_v1.md.

export const TONE_SAFE_DEFAULT = 35; // centre of "dry wit"
export const TONE_STEP = 12;         // nudge per reaction
export const TONE_CONF_GROWTH = 0.12;
export const TONE_MAX_CONF = 0.9;

export type ToneRegister = 'minimal' | 'dry' | 'playful';
export type ToneReaction = 'up' | 'keep' | 'down'; // more fun / just right / dial it down

export interface TonePreference {
  playfulness: number; // 0–100
  register: ToneRegister;
  confidence: number;  // 0–1
  samples: number;
  lastUpdated: string;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function registerFromPlayfulness(p: number): ToneRegister {
  if (p <= 25) return 'minimal';
  if (p <= 60) return 'dry';
  return 'playful';
}

export function defaultTonePreference(now: Date): TonePreference {
  return {
    playfulness: TONE_SAFE_DEFAULT,
    register: registerFromPlayfulness(TONE_SAFE_DEFAULT),
    confidence: 0.2,
    samples: 0,
    lastUpdated: now.toISOString(),
  };
}

/** Nudge the dial from a reaction. Every reaction raises confidence (we learned). */
export function updateTonePreference(
  prev: TonePreference | null,
  reaction: ToneReaction,
  now: Date,
): TonePreference {
  const base = prev ?? defaultTonePreference(now);
  let playfulness = base.playfulness;
  if (reaction === 'up') playfulness = clamp(playfulness + TONE_STEP, 0, 100);
  else if (reaction === 'down') playfulness = clamp(playfulness - TONE_STEP, 0, 100);
  return {
    playfulness,
    register: registerFromPlayfulness(playfulness),
    confidence: clamp(Math.min(TONE_MAX_CONF, base.confidence + TONE_CONF_GROWTH), 0, 1),
    samples: base.samples + 1,
    lastUpdated: now.toISOString(),
  };
}

/** Manual override pins the dial at high confidence (a strong explicit signal). */
export function setManualTone(register: ToneRegister, now: Date): TonePreference {
  const playfulness = register === 'minimal' ? 15 : register === 'dry' ? 40 : 80;
  return {
    playfulness,
    register,
    confidence: TONE_MAX_CONF,
    samples: Math.max(1, Math.round(TONE_MAX_CONF / TONE_CONF_GROWTH)),
    lastUpdated: now.toISOString(),
  };
}

/** Confidence-gated dial: at confidence 0 → safe default; at 1 → learned value. */
export function effectivePlayfulness(pref: TonePreference | null): number {
  if (!pref) return TONE_SAFE_DEFAULT;
  return Math.round(TONE_SAFE_DEFAULT + (pref.playfulness - TONE_SAFE_DEFAULT) * clamp(pref.confidence, 0, 1));
}

/** The tone line injected into the checkpoint-generation user message. */
export function toneDirective(pref: TonePreference | null): string {
  const p = effectivePlayfulness(pref);
  const band = registerFromPlayfulness(p);
  return `TONE DIAL: playfulness ${p}/100 (${band}). Calibrate Professor Cogni's wit to this level — 0 = minimal, warm and dry; 50 = dry clever wit; 100 = maximally playful — always within the validity rails. The subject-seriousness governor overrides this dial downward for grave material.`;
}
